/**
 * エントリポイント。
 *
 * 描画前に `bootstrapSession()` を await する。認証状態が未確定のまま
 * ルーターを起動すると、`beforeLoad` が「未認証」と判断して一瞬 `/login` へ飛び、
 * 直後に戻ってくる（リロードのたびに画面が跳ねる）。
 */
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, ToastProvider, useToast } from '@mail-sv/ui';
import { ApiError, setApiErrorNotifier, setUnauthorizedHandler } from './lib/api.js';
import { bootstrapSession } from './lib/auth.js';
import { ReauthGate } from './components/reauth-gate.js';
import { authStore } from './stores/auth.js';
import { router } from './router.js';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 4xx は再試行しても結果が変わらない。5xx / ネットワーク断だけ 2 回まで粘る。
      retry: (count, err) =>
        err instanceof ApiError && err.status >= 400 && err.status < 500 ? false : count < 2,
      // 運用画面はタブに戻った瞬間に最新であるべき（aurora-mail は false だが、ここは true）。
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      staleTime: 30_000,
    },
    mutations: { retry: 0 },
  },
});

/** セッション切れ時の後始末。API 層（React の外）から呼ばれる。 */
setUnauthorizedHandler(() => {
  authStore.clear();
  // 認証を跨いでキャッシュを残すと、別アカウントで入ったときに前任者のデータが一瞬見える。
  queryClient.removeQueries({ queryKey: ['ops'] });
  void router.navigate({
    to: '/login',
    search: { redirect: router.state.location.href },
    replace: true,
  });
});

/** ApiError の toast を UI に繋ぐ。ToastProvider の内側でしか出せないので子で登録する。 */
function ApiErrorBridge({ children }: { children: React.ReactNode }): React.ReactElement {
  const toast = useToast();
  React.useEffect(() => {
    setApiErrorNotifier((error) => {
      toast.error(error.message);
    });
    return () => setApiErrorNotifier(null);
  }, [toast]);
  return <>{children}</>;
}

function App(): React.ReactElement {
  return (
    <React.StrictMode>
      {/*
        既定は `system`（OS 追従）。要件 §12.2 が admin を
        「8 プリセット + `system`（既定 `system`）」と定めている。
        以前は `aurora` 固定だったので、**OS をライトにしている運営者が
        初回ログインで必ず暗い画面を見る**状態だった。
        利用者が明示的に選んだ値は `mail-sv.theme.pref` に残るので、
        既定を変えても既存の選択は上書きされない。
      */}
      <ThemeProvider storageKey="mail-sv.theme" defaultTheme="system">
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <ApiErrorBridge>
              {/*
                再認証ダイアログはルーターの外側に置く。fresh-auth が切れた
                リクエストはどの画面からでも飛ぶので、特定の画面に紐づけない。
              */}
              <ReauthGate>
                <RouterProvider router={router} />
              </ReauthGate>
            </ApiErrorBridge>
          </ToastProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </React.StrictMode>
  );
}

const container = document.getElementById('root');
if (!container) throw new Error('#root が見つかりません（index.html を確認してください）');

const root = createRoot(container);

// 認証状態が確定してから描く。失敗しても anonymous として描き出す（画面は必ず出す）。
void bootstrapSession().finally(() => {
  root.render(<App />);
});
